import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '../services/supabase';
import { ClipboardList, RefreshCw, CheckCircle, XCircle, Clock, AlertTriangle } from 'lucide-react';
import type { Session } from '@supabase/supabase-js';

interface TradeQueueRow {
    id: number;
    signal_id: string;
    symbol: string;
    signal_type: string;
    entry_price: number;
    stop_loss: number;
    take_profit: number;
    risk_type: string;
    risk_value: number;
    status: 'pending' | 'executed' | 'failed' | 'cancelled';
    retry_count: number;
    created_at: string;
    executed_at: string | null;
    error_message: string | null;
    external_trade_id: string | null;
    extra: Record<string, unknown> | null;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
    pending:   { label: 'Pending',   color: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30', icon: <Clock className="w-3 h-3" /> },
    executed:  { label: 'Executed',  color: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/30', icon: <CheckCircle className="w-3 h-3" /> },
    failed:    { label: 'Failed',    color: 'text-red-400 bg-red-400/10 border-red-400/30', icon: <XCircle className="w-3 h-3" /> },
    cancelled: { label: 'Cancelled', color: 'text-gray-400 bg-gray-400/10 border-gray-400/30', icon: <AlertTriangle className="w-3 h-3" /> },
};

export const TradeLog: React.FC<{ session: Session }> = ({ session }) => {
    const [trades, setTrades]           = useState<TradeQueueRow[]>([]);
    const [loading, setLoading]         = useState(true);
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [accountId, setAccountId]     = useState<string | null>(null);

    const fetchAccountId = useCallback(async () => {
        const { data } = await supabase
            .from('mt5_accounts')
            .select('id')
            .eq('user_id', session.user.id)
            .maybeSingle();
        setAccountId(data?.id ?? null);
        return data?.id ?? null;
    }, [session.user.id]);

    const fetchTrades = useCallback(async () => {
        setLoading(true);
        const accId = accountId ?? await fetchAccountId();
        if (!accId) { setLoading(false); return; }

        let query = supabase
            .from('mt5_trade_queue')
            .select('*')
            .eq('account_id', accId)
            .order('created_at', { ascending: false })
            .limit(100);

        if (statusFilter !== 'all') query = query.eq('status', statusFilter);

        const { data } = await query;
        setTrades((data ?? []) as TradeQueueRow[]);
        setLoading(false);
    }, [accountId, statusFilter, fetchAccountId]);

    useEffect(() => { fetchTrades(); }, [fetchTrades]);

    // Realtime subscription for this account's queue
    useEffect(() => {
        if (!accountId) return;
        const channel = supabase
            .channel('trade-queue-realtime')
            .on('postgres_changes', {
                event: '*',
                schema: 'public',
                table: 'mt5_trade_queue',
                filter: `account_id=eq.${accountId}`,
            }, () => { fetchTrades(); })
            .subscribe();
        return () => { supabase.removeChannel(channel); };
    }, [accountId, fetchTrades]);

    const formatDate = (iso: string | null) => iso
        ? new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
        : '—';

    return (
        <div className="max-w-6xl mx-auto px-4 py-8">
            <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-white flex items-center gap-2">
                        <ClipboardList className="w-8 h-8 text-finance-accent" />
                        Trade Log
                    </h1>
                    <p className="text-gray-400 mt-1">Live view of your auto-trading queue. Updated in real time.</p>
                </div>
                <div className="flex items-center gap-3">
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                        className="bg-gray-900 border border-gray-700 text-white text-sm rounded-lg px-3 py-2">
                        <option value="all">All Statuses</option>
                        <option value="pending">Pending</option>
                        <option value="executed">Executed</option>
                        <option value="failed">Failed</option>
                        <option value="cancelled">Cancelled</option>
                    </select>
                    <button onClick={fetchTrades} className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white text-sm px-4 py-2 rounded-lg border border-gray-700 transition-colors">
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                </div>
            </div>

            {!accountId && !loading && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6 text-center">
                    <AlertTriangle className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                    <p className="text-yellow-300 font-medium">No MT5 account configured.</p>
                    <p className="text-gray-400 text-sm mt-1">Go to <a href="/settings" className="underline text-finance-accent">Settings</a> to set up your auto-trading account.</p>
                </div>
            )}

            {accountId && (
                <div className="bg-finance-card border border-gray-800 rounded-xl overflow-hidden">
                    {loading ? (
                        <div className="text-center text-white py-16">Loading trades...</div>
                    ) : trades.length === 0 ? (
                        <div className="text-center text-gray-500 py-16">
                            <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-30" />
                            <p>No trades found{statusFilter !== 'all' ? ` with status "${statusFilter}"` : ''}.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-gray-800 text-gray-400 text-xs uppercase tracking-wider">
                                        <th className="px-4 py-3 text-left">ID</th>
                                        <th className="px-4 py-3 text-left">Symbol</th>
                                        <th className="px-4 py-3 text-left">Direction</th>
                                        <th className="px-4 py-3 text-right">Entry</th>
                                        <th className="px-4 py-3 text-right">SL</th>
                                        <th className="px-4 py-3 text-right">TP</th>
                                        <th className="px-4 py-3 text-left">Risk</th>
                                        <th className="px-4 py-3 text-left">Status</th>
                                        <th className="px-4 py-3 text-left">Broker Ticket</th>
                                        <th className="px-4 py-3 text-left">Queued At</th>
                                        <th className="px-4 py-3 text-left">Executed At</th>
                                        <th className="px-4 py-3 text-left">Error</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-800">
                                    {trades.map((t) => {
                                        const sc = STATUS_CONFIG[t.status];
                                        return (
                                            <tr key={t.id} className="hover:bg-gray-800/50 transition-colors">
                                                <td className="px-4 py-3 text-gray-400 font-mono">{t.id}</td>
                                                <td className="px-4 py-3 text-white font-medium">{t.symbol}</td>
                                                <td className="px-4 py-3">
                                                    <span className={`font-bold ${t.signal_type === 'BUY' ? 'text-emerald-400' : 'text-red-400'}`}>
                                                        {t.signal_type}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-right text-white font-mono">{t.entry_price.toFixed(2)}</td>
                                                <td className="px-4 py-3 text-right text-red-400 font-mono">{t.stop_loss.toFixed(2)}</td>
                                                <td className="px-4 py-3 text-right text-emerald-400 font-mono">{t.take_profit.toFixed(2)}</td>
                                                <td className="px-4 py-3 text-gray-300 text-xs">
                                                    {t.risk_type === 'equity_pct' ? `${t.risk_value}%` : `$${t.risk_value}`}
                                                    {t.retry_count > 0 && <span className="ml-1 text-orange-400">(×{t.retry_count})</span>}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border ${sc.color}`}>
                                                        {sc.icon} {sc.label}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-gray-400 font-mono text-xs">{t.external_trade_id ?? '—'}</td>
                                                <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{formatDate(t.created_at)}</td>
                                                <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{formatDate(t.executed_at)}</td>
                                                <td className="px-4 py-3 text-red-400 text-xs max-w-[200px] truncate" title={t.error_message ?? ''}>
                                                    {t.error_message ?? '—'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Stats summary */}
            {trades.length > 0 && (
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(STATUS_CONFIG).map(([key, sc]) => {
                        const count = trades.filter(t => t.status === key).length;
                        return (
                            <div key={key} className="bg-finance-card border border-gray-800 rounded-lg p-4 text-center">
                                <div className={`text-2xl font-bold ${key === 'executed' ? 'text-emerald-400' : key === 'failed' ? 'text-red-400' : key === 'pending' ? 'text-yellow-400' : 'text-gray-400'}`}>{count}</div>
                                <div className="text-xs text-gray-500 uppercase tracking-wider mt-1">{sc.label}</div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
